/**
 * @module GuardProvenanceBadge
 * @purpose Read-only display of the neutrality guard provenance for a provider briefing (FR9).
 *   Shows whether the guard fired, the repair action taken, attempt count, and rules triggered.
 * @depends types/reasoning, react-i18next
 * @dependedOnBy ProviderBriefingCompare
 *
 * @rules
 * - Performance: pure display — all data passed via props
 * - State: collapsible rules list toggled locally
 * - Errors: n/a — purely presentational
 */

import { useState } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { GuardProvenance } from '../../../../../types/reasoning'

interface GuardProvenanceBadgeProps {
  provenance: GuardProvenance
}

const CONTAINER: React.CSSProperties = {
  marginTop: '8px',
  padding: '6px 10px',
  borderRadius: '6px',
  border: '1px solid var(--border)',
  background: 'var(--bg-card)',
}

const LABEL_STYLE: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '0.65rem',
  color: 'var(--text-dim)',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
}

const VALUE_STYLE: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '0.7rem',
  color: 'var(--text)',
}

const FIRED_STYLE: React.CSSProperties = {
  ...VALUE_STYLE,
  color: 'var(--accent)',
  fontWeight: 600,
}

const CLEAN_STYLE: React.CSSProperties = {
  ...VALUE_STYLE,
  color: 'var(--text-dim)',
}

/**
 * Compact read-only display of guard provenance (FR9 — eval tool).
 *
 * @behaviour Shows guard fired status, repair action, attempt count, and (collapsible)
 *   rules triggered. Null-safe — caller must not render this when provenance is null.
 * @errors n/a — purely presentational
 */
export function GuardProvenanceBadge({ provenance }: GuardProvenanceBadgeProps): React.ReactElement {
  const { t } = useTranslation('analysis')
  const [rulesOpen, setRulesOpen] = useState(false)

  const firedLabel = provenance.guardFired
    ? t('reasoning.briefingBoard.compare.guardFired')
    : t('reasoning.briefingBoard.compare.guardClean')

  return (
    <div style={CONTAINER}>
      <div style={{ ...LABEL_STYLE, marginBottom: '4px' }}>
        {t('reasoning.briefingBoard.compare.guardTitle')}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1">
        <span style={provenance.guardFired ? FIRED_STYLE : CLEAN_STYLE}>{firedLabel}</span>
        <span>
          <span style={LABEL_STYLE}>{t('reasoning.briefingBoard.compare.repair')}: </span>
          <span style={VALUE_STYLE}>{provenance.repair}</span>
        </span>
        <span>
          <span style={LABEL_STYLE}>{t('reasoning.briefingBoard.compare.attempts')}: </span>
          <span style={VALUE_STYLE}>{String(provenance.attempts)}</span>
        </span>
        {provenance.rulesTriggered.length > 0 && (
          <button
            type="button"
            onClick={() => { setRulesOpen((prev) => !prev) }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
            aria-expanded={rulesOpen}
          >
            <span style={LABEL_STYLE}>
              {t('reasoning.briefingBoard.compare.rulesTriggered')}
              {rulesOpen
                ? <ChevronDown className="inline ml-1 h-3 w-3" />
                : <ChevronRight className="inline ml-1 h-3 w-3" />}
            </span>
          </button>
        )}
        {provenance.rulesTriggered.length === 0 && (
          <span>
            <span style={LABEL_STYLE}>{t('reasoning.briefingBoard.compare.rulesTriggered')}: </span>
            <span style={VALUE_STYLE}>{t('reasoning.briefingBoard.compare.noRules')}</span>
          </span>
        )}
      </div>
      {rulesOpen && (
        <ul style={{ marginTop: '4px', paddingLeft: '12px' }}>
          {provenance.rulesTriggered.map((rule) => (
            <li key={rule} style={VALUE_STYLE}>{rule}</li>
          ))}
        </ul>
      )}
    </div>
  )
}
